import type { SourceControlProviderType } from "@arti/persistence/sourceControl";

import z from "zod";

import { normalizeString } from "../stringUtils";

const SOURCE_CONTROL_PROVIDER_TYPES = ["github"] as const;

const requiredStringSchema = z.preprocess(
  (value) => normalizeString(value),
  z.string().min(1),
);

const optionalStringSchema = z.preprocess(
  (value) => normalizeString(value) ?? undefined,
  z.string().min(1).optional(),
);

const nullableStringSchema = z.preprocess(
  (value) => normalizeString(value) ?? null,
  z.string().min(1).nullable(),
);

const sourceControlProviderTypeSchema = z.preprocess(
  (value) => normalizeProviderType(value),
  z.enum(SOURCE_CONTROL_PROVIDER_TYPES),
);

const githubAppInstallationIdSchema = z.preprocess(
  (value) => normalizeOptionalPositiveIntegerInput(value),
  z.number().int().positive().nullable(),
);

export const sourceControlRuntimeInsightSchema = z.preprocess(
  (value) => normalizeRuntimeInsight(value),
  z
    .object({
      name: z.string().min(1).optional(),
      version: z.string().min(1).optional(),
    })
    .refine((candidate) => Boolean(candidate.name || candidate.version), {
      message: "Runtime insight must include at least a name or version.",
    }),
);

export type SourceControlRuntimeInsight = z.infer<
  typeof sourceControlRuntimeInsightSchema
>;

export const sourceControlDispatchRepositorySchema = z.object({
  defaultBranch: nullableStringSchema,
  readToken: optionalStringSchema,
  remoteSlug: requiredStringSchema,
  repositoryId: requiredStringSchema,
  runtimeInsights: z.array(sourceControlRuntimeInsightSchema),
});

export type SourceControlDispatchRepository = z.infer<
  typeof sourceControlDispatchRepositorySchema
>;

export const sourceControlDispatchContextSchema = z.object({
  baseUrl: nullableStringSchema,
  credentialId: requiredStringSchema,
  defaultOrg: nullableStringSchema,
  defaultProject: nullableStringSchema,
  githubAppInstallationId: githubAppInstallationIdSchema,
  providerId: requiredStringSchema,
  providerType: sourceControlProviderTypeSchema,
  repositories: z.array(sourceControlDispatchRepositorySchema),
  // Optional legacy fields. The actor-bound wire shape never carries them, but
  // the parser keeps them on the context so a NEW node can fall back to the
  // legacy installation-wide token when an OLD orchestrator (pre-push-broker
  // rollback) still sends the legacy payload shape.
  token: optionalStringSchema,
});

export type SourceControlDispatchContext = z.infer<
  typeof sourceControlDispatchContextSchema
>;

export const sourceControlDispatchRepositoryPayloadSchema = z.preprocess(
  (value) => normalizeDispatchRepositoryPayload(value),
  z
    .object({
      default_branch: nullableStringSchema,
      read_token: optionalStringSchema,
      remote_slug: requiredStringSchema,
      repository_id: requiredStringSchema,
      runtime_insights: z.array(sourceControlRuntimeInsightSchema),
    })
    .strict(),
);

export type SourceControlDispatchRepositoryPayload = z.infer<
  typeof sourceControlDispatchRepositoryPayloadSchema
>;

export const sourceControlDispatchPayloadSchema = z.preprocess(
  (value) => normalizeDispatchPayload(value),
  z
    .object({
      base_url: nullableStringSchema,
      credential_id: requiredStringSchema,
      default_org: nullableStringSchema,
      default_project: nullableStringSchema,
      github_app_installation_id: githubAppInstallationIdSchema,
      provider_id: requiredStringSchema,
      provider_type: sourceControlProviderTypeSchema,
      repositories: z.array(sourceControlDispatchRepositoryPayloadSchema),
      token: optionalStringSchema,
    })
    .strict(),
);

export type SourceControlDispatchPayload = z.infer<
  typeof sourceControlDispatchPayloadSchema
>;

/**
 * Serializes a dispatch context to its wire shape. `legacy` selects the
 * pre-push-broker shape (top-level installation-wide token, real
 * `github_app_installation_id`, no per-repository `read_token`): the ONLY
 * shape an OLD node's strict schema accepts. It exists solely for the
 * mixed-version rolling-deploy window; once the fleet is fully upgraded the
 * capability gate stops selecting it. The actor-bound shape (no token, null
 * installation id, per-repo `read_token`) remains the default.
 */
export function serializeSourceControlDispatchContext(
  context: SourceControlDispatchContext,
  options?: { legacy?: boolean },
): SourceControlDispatchPayload {
  const normalizedContext = sourceControlDispatchContextSchema.parse(context);
  const legacy = options?.legacy === true;

  return sourceControlDispatchPayloadSchema.parse({
    base_url: normalizedContext.baseUrl,
    credential_id: normalizedContext.credentialId,
    default_org: normalizedContext.defaultOrg,
    default_project: normalizedContext.defaultProject,
    github_app_installation_id: legacy
      ? normalizedContext.githubAppInstallationId
      : null,
    provider_id: normalizedContext.providerId,
    provider_type: normalizedContext.providerType,
    repositories: normalizedContext.repositories.map((repository) => ({
      default_branch: repository.defaultBranch,
      read_token: legacy ? undefined : repository.readToken,
      remote_slug: repository.remoteSlug,
      repository_id: repository.repositoryId,
      runtime_insights: serializeRuntimeInsights(repository.runtimeInsights),
    })),
    ...(legacy && normalizedContext.token !== undefined
      ? { token: normalizedContext.token }
      : {}),
  });
}

export function parseSourceControlDispatchContext(
  value: unknown,
): SourceControlDispatchContext | null {
  const parsed = sourceControlDispatchPayloadSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  return sourceControlDispatchContextSchema.parse({
    baseUrl: parsed.data.base_url,
    credentialId: parsed.data.credential_id,
    defaultOrg: parsed.data.default_org,
    defaultProject: parsed.data.default_project,
    githubAppInstallationId: parsed.data.github_app_installation_id,
    providerId: parsed.data.provider_id,
    providerType: parsed.data.provider_type,
    repositories: parsed.data.repositories.map((repository) => ({
      defaultBranch: repository.default_branch,
      readToken: repository.read_token,
      remoteSlug: repository.remote_slug,
      repositoryId: repository.repository_id,
      runtimeInsights: repository.runtime_insights,
    })),
    // Omit the key entirely when absent: zod keeps an explicitly-undefined
    // optional key on the parsed object, which would break the
    // "actor-bound payload carries no token" invariant.
    ...(parsed.data.token !== undefined ? { token: parsed.data.token } : {}),
  });
}

function normalizeProviderType(
  value: unknown,
): SourceControlProviderType | null {
  if (typeof value !== "string") {
    return null;
  }

  switch (value.trim().toLowerCase()) {
    case "github": {
      return "github";
    }
    default: {
      return null;
    }
  }
}

function normalizeOptionalPositiveIntegerInput(value: unknown): unknown {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "string" && value.trim() === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }

  if (typeof value === "string") {
    const parsed = Number.parseInt(value.trim(), 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return value;
}

function normalizeDispatchPayload(value: unknown): unknown {
  const record = normalizeUnknownObject(value);
  if (!record) {
    return value;
  }

  const normalized: Record<string, unknown> = {
    base_url: record.base_url ?? record.baseUrl ?? null,
    credential_id: record.credential_id ?? record.credentialId,
    default_org: record.default_org ?? record.defaultOrg ?? null,
    default_project: record.default_project ?? record.defaultProject ?? null,
    github_app_installation_id:
      record.github_app_installation_id ??
      record.githubAppInstallationId ??
      null,
    provider_id: record.provider_id ?? record.providerId,
    provider_type: record.provider_type ?? record.providerType,
    repositories: record.repositories,
  };
  // Optional fields are omitted when absent so the serialized payload never
  // carries a `token: undefined`-style key.
  if (record.token !== undefined) {
    normalized.token = record.token;
  }
  return normalized;
}

function normalizeDispatchRepositoryPayload(value: unknown): unknown {
  const record = normalizeUnknownObject(value);
  if (!record) {
    return value;
  }

  const normalized: Record<string, unknown> = {
    default_branch: record.default_branch ?? record.defaultBranch ?? null,
    remote_slug: record.remote_slug ?? record.remoteSlug,
    repository_id: record.repository_id ?? record.repositoryId,
    runtime_insights: record.runtime_insights ?? record.runtimeInsights ?? [],
  };
  if (record.read_token !== undefined || record.readToken !== undefined) {
    normalized.read_token = record.read_token ?? record.readToken;
  }
  return normalized;
}

function normalizeRuntimeInsight(value: unknown): unknown {
  const record = normalizeUnknownObject(value);
  if (!record) {
    return value;
  }

  return {
    name: normalizeString(record.name) ?? undefined,
    version: normalizeString(record.version) ?? undefined,
  };
}

function serializeRuntimeInsights(
  insights: SourceControlRuntimeInsight[] | undefined,
): SourceControlRuntimeInsight[] {
  return z.array(sourceControlRuntimeInsightSchema).parse(insights ?? []);
}

function normalizeUnknownObject(
  value: unknown,
): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return Object.fromEntries(Object.entries(value));
}
